/**
 * AuthCodeModal - A modal component for email verification flow
 *
 * This modal replaces the VerifyLayout from the stack context and can be triggered
 * from anywhere in the app. It slides up from the bottom of the screen and covers
 * the entire screen, using the same transition logic as StackScreen.
 *
 * Features:
 * - Slides up from bottom with smooth animation
 * - Covers entire screen with backdrop
 * - Handles email verification flow (send code -> enter code -> verify)
 * - Integrates with existing AuthCode component
 * - Uses same styling and behavior as original VerifyLayout
 *
 * Usage:
 * ```tsx
 * const { openAuthCodeModal } = useAuthCodeModal();
 *
 * openAuthCodeModal({
 *   title: "Verify Transaction",
 *   description: "Please verify this transaction",
 *   onSuccess: () => console.log('Verified!')
 * });
 * ```
 */

import { GCashButton } from '@src/components/Button';
import { useUserContext } from '@talos/kyoko';
import { CustomEventType, type MFAResultEventDetails } from '@talos/kyoko/src/tokens/event';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import emailIcon from '../assets/svg/email.svg';
import { useRequestAuthCode, useSubmitAuthCode } from '../pages/Verify/verifyCode';
import { AuthCode } from './AuthCode';

const AUTH_CODE_LENGTH = 6;

export interface AuthCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
}

export const AuthCodeModal = ({
  isOpen,
  onClose,
  onSuccess,
  title = 'Verify via email',
  description,
}: AuthCodeModalProps) => {
  const { user } = useUserContext();
  const [userCode, setUserCode] = useState('');
  const [step, setStep] = useState<'idle' | 'code_requested' | 'code_submitted' | 'code_error'>('idle');
  const [error, setError] = useState<string | undefined>(undefined);

  const updateAuthCode = useCallback((res: string) => {
    setUserCode(res);
    setError(undefined);
  }, []);

  const buttonLabel = step === 'idle' ? 'Send Code' : 'Submit';
  const buttonDisabled = step === 'code_requested' && userCode.length !== AUTH_CODE_LENGTH;

  const { mutateAsync: requestAuthCode, state: requestState } = useRequestAuthCode();
  const { mutateAsync: submitAuthCode, state: submitState } = useSubmitAuthCode();
  const isRequesting = requestState === 'pending';
  const isSubmitting = submitState === 'pending';
  const loading = isRequesting || isSubmitting;

  const handleButtonClick = useCallback(async () => {
    if (step === 'idle') {
      // Simulate sending the code
      await requestAuthCode();
      setStep('code_requested');
    } else if (step === 'code_requested') {
      // Handle submission
      try {
        const res = await submitAuthCode(userCode);
        if (res) {
          onSuccess?.();
        }
      } catch (error) {
        setError('Invalid code');
      }
    }
  }, [onSuccess, requestAuthCode, step, submitAuthCode, userCode]);

  const handleClose = useCallback(() => {
    // Reset state when closing
    setStep('idle');
    setUserCode('');
    setError(undefined);
    onClose();
    // dispatch a custom event to the document window to indicate that 2fa is dismissed.
    document.dispatchEvent(
      new CustomEvent<MFAResultEventDetails>(CustomEventType.MFA_RESULT, { detail: { result: 'dismissed' } })
    );
  }, [onClose]);

  const defaultDescription =
    step === 'idle'
      ? `A 6-digit code will be sent to ${user.Email}`
      : 'Please enter the 6-digit authentication code sent to your email.';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed inset-0 z-50 flex items-end justify-center"
          onClick={handleClose}
          data-testid="verify-modal"
        >
          {/* Modal Content */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="relative size-full bg-[#F6F9FD] shadow-2xl"
            onClick={e => e.stopPropagation()}
            style={{ boxShadow: '-20px 30px 30px 0px rgba(0,0,0,0.6)' }}
          >
            <div className="flex size-full min-h-[70vh] flex-col items-center justify-between p-6 text-center">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 flex size-8 items-center justify-center rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 focus:ring-2 focus:ring-gray-400 focus:outline-none"
                aria-label="Close modal"
                data-testid="auth-code-modal-close"
              >
                <svg
                  className="size-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Top content */}
              <div className="mt-8 flex flex-col items-center">
                {/* Email verification icon */}

                <img src={emailIcon} alt="Email Icon" className="mb-5 size-14" data-testid="email-icon" />

                {/* Heading */}
                <div>
                  <h1 className="text-color-text mb-2 text-xl font-bold">{title}</h1>
                  <p className="text-color-text-weaker text-sm font-semibold">{description || defaultDescription}</p>
                </div>
              </div>

              <AnimatePresence>
                {step !== 'idle' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    <AuthCode onChange={updateAuthCode} length={AUTH_CODE_LENGTH} error={error} />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Bottom action and disclaimer */}
              <div className="flex w-full flex-col items-center">
                {step === 'idle' && (
                  <p className="text-color-text p-4 text-xs font-semibold">
                    Official GCash representatives will never ask for your authentication code.
                  </p>
                )}

                <GCashButton
                  onClick={handleButtonClick}
                  disabled={buttonDisabled || !!error || loading}
                  size="md"
                  variant="primary"
                  className="relative w-full"
                  data-testid="verify-submit-button"
                >
                  {loading ? 'loading...' : buttonLabel}
                </GCashButton>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
